import * as React from 'react';
import { LogInIcon } from 'lucide-react';

import { useSession } from '@/components/auth/user-provider';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';

const SIGN_IN_START_ERROR = 'Sign-in could not start. Please try again or contact your organisation’s IT support team.';

export interface PublicSignInAction {
  start: () => void;
  busy: boolean;
  disabled: boolean;
  label: string;
  error: string | null;
}

/** The existing signed-out sign-in action, kept on the click gesture for embedded handoff. */
export function usePublicSignIn(): PublicSignInAction {
  const { session, signIn } = useSession();
  const { ready } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const pending = React.useRef(false);
  const timer = React.useRef<number | null>(null);

  React.useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  const start = () => {
    if (pending.current || !ready || session.status !== 'signedOut') return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      signIn();
      timer.current = window.setTimeout(() => {
        pending.current = false;
        setBusy(false);
      }, 1500);
    } catch {
      pending.current = false;
      setBusy(false);
      setError(SIGN_IN_START_ERROR);
    }
  };

  const checking = session.status === 'loading' || !ready;
  return {
    start,
    busy,
    disabled: busy || checking,
    label: busy ? 'Opening sign-in…' : checking ? 'Checking sign-in…' : 'Sign in',
    error
  };
}

export function PublicSignInButton({ action, size = 'sm' }: { action: PublicSignInAction; size?: 'sm' | 'lg' }) {
  return (
    <Button type="button" size={size} onClick={action.start} disabled={action.disabled} className="gap-2">
      <LogInIcon className="size-4" aria-hidden />
      {action.label}
    </Button>
  );
}

/** Sign-out confirmation and sign-in failures shown on signed-out screens. */
export function PublicAuthNotices({ signInError }: { signInError: string | null }) {
  const { interactionError, localSignedOut, signOutError, logout } = useAuth();
  const message = signInError ?? interactionError;
  return (
    <>
      {localSignedOut ? <section aria-label="Sign-out status" className="bg-muted/50 space-y-3 rounded-lg border p-4 text-sm">
        <p role="status">You are signed out of this panel. Microsoft sign-out is unconfirmed.</p>
        <p className="text-muted-foreground">Finish sign-out in the new tab. If no tab appears, allow new tabs and retry. This panel stays signed out. To sign in again, use Sign in and continue in the new tab.</p>
        {signOutError ? <p role="alert">{signOutError}</p> : null}
        <Button type="button" variant="outline" onClick={logout}>Retry sign-out</Button>
      </section> : null}
      {message ? <div role="alert" className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border p-4 text-sm">{message}</div> : null}
    </>
  );
}
